import localImpactSound from "../../../assets/sound/impact.mp3";

/** Encode each path segment; decode first so already-encoded keys are not double-encoded. */
export function encodeThemePathSegments(pathPart) {
  return String(pathPart)
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
}

export function normalizeHttpThemeAssetUrl(raw) {
  const trimmed = String(raw).trim();
  const m = trimmed.match(/^(https?:\/\/[^/?#]+)(\/[^?#]*)?(\?[^#]*)?(#.*)?$/i);
  if (!m) return trimmed;
  const origin = m[1];
  const pathSlash = m[2] || "";
  const search = m[3] || "";
  const hash = m[4] || "";
  const pathPart = pathSlash.replace(/^\//, "");
  if (!pathPart) return `${origin}${search}${hash}`;
  const encoded = encodeThemePathSegments(pathPart);
  return `${origin}/${encoded}${search}${hash}`;
}

export function buildThemeAssetUrl(asset) {
  if (asset == null) return "";
  const raw = String(asset).trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const pathOnly = u.pathname.replace(/^\//, "");
      if (!pathOnly) return `${u.origin}${u.search}${u.hash}`;
      const encoded = encodeThemePathSegments(pathOnly);
      return `${u.origin}/${encoded}${u.search}${u.hash}`;
    } catch {
      return normalizeHttpThemeAssetUrl(raw);
    }
  }

  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/+$/, "");
  if (!base) return raw;
  const encoded = encodeThemePathSegments(raw);
  return `${base}/${encoded}`;
}

export function themeSpriteBasename(asset) {
  const raw = String(asset ?? "").trim();
  if (!raw) return "";
  const path = raw.split("?")[0].split("#")[0];
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      const seg = u.pathname.split("/").filter(Boolean);
      return seg[seg.length - 1] || "";
    } catch {
      return "";
    }
  }
  const segments = path.replace(/^\/+/, "").split("/");
  return segments[segments.length - 1] || path;
}

export function buildLocalThemeSpriteUrl(asset) {
  const name = themeSpriteBasename(asset);
  if (!name) return "";
  const publicBase = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${publicBase}/img/${encodeURIComponent(name)}`;
}

export async function checkImageExists(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export async function pickReachableImageUrl(s3Url, localUrl) {
  if (s3Url && (await checkImageExists(s3Url))) return s3Url;
  if (localUrl && (await checkImageExists(localUrl))) return localUrl;
  return s3Url || localUrl || "";
}

export async function resolveThemeSpriteUrl(sprite) {
  const raw = String(sprite ?? "").trim();
  if (!raw) return "";
  const primaryUrl = buildThemeAssetUrl(raw);
  const localUrl = /^https?:\/\//i.test(raw) ? "" : buildLocalThemeSpriteUrl(raw);
  return pickReachableImageUrl(primaryUrl, localUrl);
}

function normalizeImpactThemeValue(v) {
  if (v == null) return "";
  if (Array.isArray(v)) {
    const first = v.find((x) => x != null && String(x).trim());
    return first != null ? String(first).trim() : "";
  }
  if (typeof v === "string") return v.trim();
  const s = String(v).trim();
  return /^[\s\[]*[{\[]/.test(s) ? "" : s;
}

const ENV_IMPACT_SOUND_URL = String(process.env.REACT_APP_IMPACT_SOUND_URL || "").trim();
const ENV_IMPACT_SOUND_KEY = String(
  process.env.REACT_APP_IMPACT_SOUND_KEY ?? process.env.REACT_APP_IMPACT_S3_FILENAME ?? ""
).trim();
const DEFAULT_S3_IMPACT_FILENAME = "impact.mp3";

export function resolveImpactSoundUrl(theme) {
  if (ENV_IMPACT_SOUND_URL) {
    if (/^https?:\/\//i.test(ENV_IMPACT_SOUND_URL)) {
      return ENV_IMPACT_SOUND_URL;
    }
    const fromEnv = buildThemeAssetUrl(ENV_IMPACT_SOUND_URL);
    if (fromEnv) return fromEnv;
  }
  if (ENV_IMPACT_SOUND_KEY) {
    const fromKey = buildThemeAssetUrl(ENV_IMPACT_SOUND_KEY);
    if (fromKey) return fromKey;
  }
  const raw = normalizeImpactThemeValue(theme?.splash_sound || theme?.impact_sound);
  if (raw) {
    const resolved = buildThemeAssetUrl(raw);
    if (resolved) return resolved;
  }
  const s3Configured = Boolean(String(process.env.REACT_APP_S3_PATH || "").trim());
  if (s3Configured) {
    const fallback = buildThemeAssetUrl(DEFAULT_S3_IMPACT_FILENAME);
    if (fallback) return fallback;
  }
  return localImpactSound;
}

export function resolveThemeBgMusicUrl(theme) {
  const raw = normalizeImpactThemeValue(theme?.audio);
  if (!raw) return "";
  return buildThemeAssetUrl(raw);
}

/** Safe for `element.style.backgroundImage` */
export function cssBackgroundImageUrl(url) {
  if (!url) return "none";
  const escaped = String(url).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Background choice helper:
 * - Portrait phones (<=768px wide) use mobile backgrounds.
 * - Landscape on phones uses desktop backgrounds to avoid portrait "mobile" assets.
 */
export function shouldUseMobileBackground() {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;
  const isNarrow = w <= 768;
  const isPortrait =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(orientation: portrait)").matches
      : h >= w;
  return isNarrow && isPortrait;
}
